/**
 * The owner's accountKey on this browser (builder page). Storage lives in tokens.ts
 * (key `circles.accountKey`, architecture §12); this adds a React hook that re-renders
 * when the key changes in this tab or another one.
 */
import { useSyncExternalStore } from 'react';
import { STORAGE_KEYS } from '@circles/convex';
import { getAccountKey, setAccountKey } from './tokens';

const CHANGE_EVENT = 'circles:account-key';

function subscribe(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === STORAGE_KEYS.accountKey) onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

export function useAccountKey(): string | null {
  return useSyncExternalStore(subscribe, getAccountKey, () => null);
}

/**
 * Stores (or, with null, forgets) the key and notifies every `useAccountKey` in this tab.
 * `keep` (default true) is the sheet's "Keep me signed in on this browser": unchecked stores the
 * key for this tab only (`sessionStorage`, §14.78). Forgetting clears both storages.
 */
export function saveAccountKey(accountKey: string | null, opts?: { keep?: boolean }): void {
  setAccountKey(accountKey, opts);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** `abcd••••••••wxyz` — enough to recognise a key without exposing it. */
export function maskAccountKey(accountKey: string): string {
  if (accountKey.length <= 8) return '•'.repeat(accountKey.length);
  return `${accountKey.slice(0, 4)}${'•'.repeat(8)}${accountKey.slice(-4)}`;
}
